import { CircleCheck, CircleX, Server } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import { Badge } from '../../components/Badge';
import { DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { RefreshButton } from './shared';

const servicePath = (name: string): string => `/ops/services/${encodeURIComponent(name)}`;

interface ServiceGroup {
  service: string;
  up: number;
  total: number;
  instances: { instance: string; up: boolean }[];
}

/** `/ops/services`: every service, its instances and whether they answer. */
export function ServicesPage() {
  const { t } = useTranslation('ops');
  useDocumentTitle(t('services.title'));
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  const problemsOnly = params.get('problems') === '1';
  const result = useApi((signal) => api.listOpsServices({ signal }), []);
  const unlabelled = t('services.unlabelled');

  const groups = new Map<string, ServiceGroup>();
  if (result.status === 'success') {
    for (const s of result.data.items) {
      const key = s.service ?? unlabelled;
      const g = groups.get(key) ?? { service: key, up: 0, total: 0, instances: [] };
      g.total += 1;
      if (s.up) g.up += 1;
      g.instances.push({ instance: s.instance ?? '—', up: s.up });
      groups.set(key, g);
    }
  }
  const all = [...groups.values()].sort((x, y) => x.up / x.total - y.up / y.total || x.service.localeCompare(y.service));
  const needle = query.trim().toLowerCase();
  const rows = all.filter((g) => (!needle || g.service.toLowerCase().includes(needle)) && (!problemsOnly || g.up < g.total));
  const up = all.reduce((a, g) => a + g.up, 0);
  const total = all.reduce((a, g) => a + g.total, 0);

  const setParam = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  const columns: Column<ServiceGroup>[] = [
    {
      key: 'service',
      header: t('services.col.service'),
      cell: (g) => (g.service === unlabelled ? <span className="font-medium">{g.service}</span> : <EntityRef label={g.service} to={servicePath(g.service)} />),
    },
    {
      key: 'health',
      header: t('services.col.health'),
      cell: (g) => <Badge tone={g.up === g.total ? 'ok' : g.up === 0 ? 'crit' : 'warn'}>{t('services.health', { up: g.up, total: g.total })}</Badge>,
    },
    {
      key: 'instances',
      header: t('services.col.instances'),
      cell: (g) => (
        <ul className="space-y-1">
          {g.instances.map((i) => (
            <li key={i.instance} className="flex items-center gap-1.5 font-mono text-xs">
              {i.up ? <CircleCheck className="size-3.5 text-success" aria-label={t('services.up')} /> : <CircleX className="size-3.5 text-critical" aria-label={t('services.down')} />}
              <span className="break-all">{i.instance}</span>
              {i.up ? null : <span className="font-sans font-semibold text-critical">{t('services.down')}</span>}
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('services.title')} subtitle={t('services.subtitle')} actions={<RefreshButton onClick={result.reload} busy={result.status === 'loading'} />} />
      {result.status === 'success' && all.length ? (
        <StatGrid className="mb-6 xl:grid-cols-3">
          <StatCard icon={Server} label={t('services.stat.services')} value={formatCount(all.length)} />
          <StatCard icon={CircleCheck} label={t('services.stat.up')} value={`${formatCount(up)}/${formatCount(total)}`} tone={up === total ? 'ok' : 'warn'} hint={up === total ? t('services.stat.allUp') : undefined} />
          <StatCard icon={CircleX} label={t('services.stat.down')} value={formatCount(total - up)} tone={total - up ? 'crit' : 'neutral'} hint={t('services.stat.downHint')} />
        </StatGrid>
      ) : null}
      <FilterBar>
        <Field label={t('services.filter')} className="w-full sm:w-64">
          <Input
            type="search"
            value={query}
            placeholder={t('services.filterPlaceholder')}
            onChange={(e) => {
              setQuery(e.target.value);
              setParam('q', e.target.value.trim());
            }}
          />
        </Field>
        <label className="flex h-9 items-center gap-2 text-sm">
          <Checkbox checked={problemsOnly} onCheckedChange={(v) => setParam('problems', v === true ? '1' : '')} />
          {t('services.onlyProblems')}
        </label>
      </FilterBar>
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={4} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('services.loadFailed')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(g) => g.service}
            onRowClick={(g) => (g.service === unlabelled ? undefined : navigate(servicePath(g.service)))}
            empty={<EmptyState title={all.length ? t('services.noMatch') : t('services.empty')} />}
          />
        ) : null}
      </Section>
    </>
  );
}
